import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Download, FileText, Zap, ClipboardList, Send, Ban, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const inp = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";
const emptyItem = { description: "", qty: 1, unit_price: 0 };

export default function InvoicesView() {
  const [invoices, setInvoices] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [orders, setOrders] = useState([]);
  const [company, setCompany] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_address: "", notes: "6 Monate Garantie auf Display-Reparaturen mit Original-OLED. Keine zusätzliche Garantie auf Verschleißteile (z. B. Akku) bzw. LCD-Displays. Vielen Dank für Ihr Vertrauen." });
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [creating, setCreating] = useState(false);
  const [quick, setQuick] = useState({ model: "", variant: "oled" });
  const [fromOrder, setFromOrder] = useState("");

  const load = async () => {
    try {
      const [inv, pr, ord, co, st] = await Promise.all([api.get("/invoices"), api.get("/pricing"), api.get("/orders"), api.get("/company"), api.get("/service-types")]);
      setInvoices(inv.data);
      setPricing(pr.data);
      setOrders(ord.data);
      setCompany(co.data);
      setServiceTypes(st.data);
      setQuick((q) => ({ ...q, model: pr.data[0]?.model || "" }));
    } catch (e) { toast.error("Daten konnten nicht geladen werden."); }
  };
  useEffect(() => { load(); }, []);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((p) => [...p, { ...emptyItem }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const applyOrder = (orderId) => {
    setFromOrder(orderId);
    const o = orders.find((x) => x.id === orderId);
    if (!o) return;
    setForm((f) => ({
      ...f,
      customer_name: `${o.first_name} ${o.last_name}`,
      customer_email: o.email,
      customer_address: `${o.street}\n${o.zip} ${o.city}`,
    }));
    const row = pricing.find((p) => p.model === o.device);
    const dispType = o.display_type === "lcd" ? "LCD" : "OLED";
    const lines = [];
    const singleFallback = (!o.wants_display && (o.service_keys || []).length === 1) ? o.base_price : 0;
    if (o.wants_display) {
      const dp = row ? (o.display_type === "lcd" ? row.lcd_price : row.oled_price) : o.base_price;
      lines.push({ description: `Display-Reparatur (${dispType}) – ${o.device}`, qty: 1, unit_price: dp });
    }
    (o.service_keys || []).forEach((key) => {
      const name = serviceTypes.find((s) => s.key === key)?.name || key;
      const price = row?.service_prices?.[key]?.sell_price ?? singleFallback;
      lines.push({ description: `${name} – ${o.device}`, qty: 1, unit_price: price });
    });
    lines.push({ description: "Versand (Hin- & Rückweg)", qty: 1, unit_price: o.shipping });
    setItems(lines);
    toast.success("Daten aus Bestellung übernommen (Positionen aufgesplittet).");
  };

  const priceFor = (row, variant) => {
    if (variant === "oled") return row.oled_price;
    if (variant === "lcd") return row.lcd_price;
    return row.service_prices?.[variant]?.sell_price || 0;
  };
  const labelFor = (variant) => {
    if (variant === "oled") return "Display-Reparatur (OLED)";
    if (variant === "lcd") return "Display-Reparatur (LCD)";
    return serviceTypes.find((s) => s.key === variant)?.name || variant;
  };

  const addFromPrice = () => {
    const row = pricing.find((r) => r.model === quick.model);
    if (!row) return;
    setItems((p) => {
      const base = p.length === 1 && !p[0].description ? [] : p;
      return [...base, { description: `${labelFor(quick.variant)} ${row.model}`, qty: 1, unit_price: priceFor(row, quick.variant) }];
    });
    toast.success("Position hinzugefügt.");
  };

  const ku = !!company?.kleinunternehmer;
  const rate = ku ? 0 : Number(company?.tax_rate || 0);
  const gross = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const net = rate ? gross / (1 + rate / 100) : gross;
  const tax = gross - net;
  const total = gross;

  const create = async (e) => {
    e.preventDefault();
    const valid = items.filter((it) => it.description.trim());
    if (!form.customer_name.trim() || valid.length === 0) {
      toast.error("Kundenname und mindestens eine Position erforderlich.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/invoices", {
        customer_name: form.customer_name, customer_email: form.customer_email,
        customer_address: form.customer_address, notes: form.notes,
        items: valid.map((it) => ({ description: it.description, qty: Number(it.qty), unit_price: Number(it.unit_price) })),
      });
      toast.success("Rechnung erstellt.");
      setForm((f) => ({ ...f, customer_name: "", customer_email: "", customer_address: "" }));
      setItems([{ ...emptyItem }]); setFromOrder("");
      load();
    } catch (e) { toast.error("Erstellung fehlgeschlagen."); }
    finally { setCreating(false); }
  };

  const downloadPdf = async (inv) => {
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) win.location.href = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      if (win) win.close();
      toast.error("PDF konnte nicht geladen werden.");
    }
  };

  const remove = async (inv) => {
    try {
      await api.delete(`/invoices/${inv.id}`);
      setInvoices((p) => p.filter((x) => x.id !== inv.id));
      toast.success("Rechnung gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const togglePaid = async (inv) => {
    try {
      const { data } = await api.patch(`/invoices/${inv.id}/paid`, { paid: !inv.paid });
      setInvoices((p) => p.map((x) => (x.id === inv.id ? data : x)));
      toast.success(data.paid ? "Als bezahlt markiert." : "Als offen markiert.");
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); }
  };

  const toggleCancelled = async (inv) => {
    try {
      const { data } = await api.patch(`/invoices/${inv.id}/cancel`, { cancelled: !inv.cancelled });
      setInvoices((p) => p.map((x) => (x.id === inv.id ? data : x)));
      toast.success(data.cancelled ? "Rechnung storniert." : "Stornierung aufgehoben.");
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); }
  };

  const [sendingId, setSendingId] = useState(null);
  const sendInvoice = async (inv) => {
    if (!inv.customer_email) { toast.error("Keine Kunden-E-Mail hinterlegt."); return; }
    setSendingId(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/send`, { base_url: window.location.origin });
      toast.success(`Rechnung an ${data.to} gesendet.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Senden fehlgeschlagen."); }
    finally { setSendingId(null); }
  };

  return (
    <div data-testid="invoices-view">
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-8">Rechnungen</h1>

      <div className="space-y-10">
        <form onSubmit={create} data-testid="invoice-form" className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6 lg:p-8 space-y-5">
          <h2 className="font-display text-2xl">Neue Rechnung</h2>

          {/* From order */}
          <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl border border-white/10 bg-[#050505]">
            <ClipboardList size={16} className="text-[#888]" />
            <span className="text-sm text-[#aaa]">Aus Bestellung:</span>
            <select data-testid="from-order" value={fromOrder} onChange={(e) => applyOrder(e.target.value)} className="flex-1 min-w-[200px] bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
              <option value="" className="bg-[#0a0a0a]">Kunde auswählen…</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id} className="bg-[#0a0a0a]">{o.first_name} {o.last_name} · {o.device} · {o.total.toFixed(2)} €</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <input data-testid="inv-name" required value={form.customer_name} onChange={setF("customer_name")} placeholder="Kundenname" className={inp} />
            <input data-testid="inv-email" value={form.customer_email} onChange={setF("customer_email")} placeholder="E-Mail" className={inp} />
            <textarea rows={1} data-testid="inv-address" value={form.customer_address} onChange={setF("customer_address")} placeholder="Adresse (Straße, PLZ Ort)" className={`${inp} resize-none`} />
          </div>

          {/* Quick add from price list */}
          <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl border border-white/10 bg-[#050505]">
            <Zap size={16} className="text-[#888]" />
            <select data-testid="quick-model" value={quick.model} onChange={(e) => setQuick((q) => ({ ...q, model: e.target.value }))} className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
              {pricing.map((r) => <option key={r.id} value={r.model} className="bg-[#0a0a0a]">{r.model}</option>)}
            </select>
            <select data-testid="quick-variant" value={quick.variant} onChange={(e) => setQuick((q) => ({ ...q, variant: e.target.value }))} className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
              <option value="oled" className="bg-[#0a0a0a]">Display OLED</option>
              <option value="lcd" className="bg-[#0a0a0a]">Display LCD</option>
              {serviceTypes.map((s) => <option key={s.key} value={s.key} className="bg-[#0a0a0a]">{s.name}</option>)}
            </select>
            <button type="button" onClick={addFromPrice} data-testid="quick-add" className="text-sm rounded-lg border border-white/15 px-3 py-2 hover:bg-white hover:text-[#050505] transition-colors">Aus Preisliste</button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`item-row-${i}`}>
                  <input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Beschreibung" className={`${inp} flex-1`} data-testid={`item-desc-${i}`} />
                  <input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className={`${inp} w-16 text-center`} />
                  <input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className={`${inp} w-24 text-center`} data-testid={`item-price-${i}`} />
                  <button type="button" onClick={() => removeItem(i)} className="w-9 h-9 shrink-0 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                </div>
              ))}
              <button type="button" onClick={addItem} data-testid="add-item" className="inline-flex items-center gap-2 text-sm text-[#aaa] hover:text-white transition-colors"><Plus size={15} /> Position hinzufügen</button>
            </div>

            <div className="space-y-4">
              <div data-testid="tax-mode" className="flex items-center gap-2 text-sm text-[#888] rounded-xl border border-white/10 bg-[#050505] px-4 py-3">
                {ku
                  ? "Kleinunternehmer (§ 19 UStG) – keine Umsatzsteuer. Änderbar unter Firmendaten."
                  : `Umsatzsteuer ${rate}% wird ausgewiesen. Änderbar unter Firmendaten.`}
              </div>
              <textarea rows={2} value={form.notes} onChange={setF("notes")} placeholder="Notiz / Zahlungshinweis" className={`${inp} resize-none`} />
              <div className="rounded-xl bg-[#050505] border border-white/10 p-4 space-y-1 text-sm">
                {!ku && rate > 0 && (
                  <div className="flex justify-between text-[#999]"><span>darin enthaltene MwSt. ({rate}%)</span><span>{tax.toFixed(2)} €</span></div>
                )}
                <div className="flex justify-between text-white text-lg font-medium pt-1"><span>Gesamtbetrag</span><span data-testid="inv-total">{total.toFixed(2)} €</span></div>
              </div>
              <button type="submit" disabled={creating} data-testid="create-invoice" className="w-full rounded-full bg-white text-[#050505] py-3.5 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
                {creating ? "Erstellen…" : "Rechnung erstellen"}
              </button>
            </div>
          </div>
        </form>

        <div>
          <h2 className="font-display text-2xl mb-4">Erstellte Rechnungen</h2>
          {invoices.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-12 text-center text-[#888]" data-testid="invoices-empty">
              <FileText size={28} className="mx-auto mb-3 opacity-50" />
              Noch keine Rechnungen erstellt.
            </div>
          ) : (
            <div className="space-y-3" data-testid="invoices-list">
              {invoices.map((inv) => (
                <motion.div key={inv.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={`invoice-${inv.id}`}
                  className={`rounded-2xl border p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 transition-colors ${inv.cancelled ? "border-red-500/20 bg-[#0a0a0a]/60 opacity-60" : "border-white/10 bg-[#0a0a0a]"}`}>
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-sm text-white">{inv.invoice_number}</span>
                      <span className="text-xs text-[#666]">{new Date(inv.created_at).toLocaleDateString("de-DE")}</span>
                      {inv.cancelled ? (
                        <span data-testid={`cancelled-badge-${inv.id}`} className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
                          Storniert
                        </span>
                      ) : (
                        <span data-testid={`paid-badge-${inv.id}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${inv.paid ? "bg-green-500/20 text-green-300 border border-green-500/30" : "bg-amber-500/20 text-amber-300 border border-amber-500/30"}`}>
                          {inv.paid ? "Bezahlt" : "Offen"}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm mt-1 ${inv.cancelled ? "text-[#888]" : "text-[#ccc]"}`}>
                      {inv.customer_name}
                      {inv.customer_number && <span data-testid={`customer-number-${inv.id}`} className="ml-2 font-mono text-xs text-emerald-300/80">· {inv.customer_number}</span>}
                    </p>
                    <p className={`font-display text-2xl mt-1 ${inv.cancelled ? "line-through text-[#888]" : ""}`}>{inv.total.toFixed(2)} €</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!inv.cancelled && (
                      <button onClick={() => togglePaid(inv)} data-testid={`toggle-paid-${inv.id}`} title="Bezahlt-Status umschalten"
                        className={`inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 transition-colors border ${inv.paid ? "border-white/15 text-[#ccc] hover:text-white" : "border-green-500/40 text-green-300 hover:bg-green-500/10"}`}>
                        {inv.paid ? "Als offen" : "Als bezahlt"}
                      </button>
                    )}
                    <button onClick={() => toggleCancelled(inv)} data-testid={`toggle-cancel-${inv.id}`} title={inv.cancelled ? "Stornierung aufheben" : "Rechnung stornieren"}
                      className={`inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 transition-colors border ${inv.cancelled ? "border-white/15 text-[#ccc] hover:text-white" : "border-red-500/40 text-red-300 hover:bg-red-500/10"}`}>
                      {inv.cancelled ? <><RotateCcw size={15} /> Reaktivieren</> : <><Ban size={15} /> Stornieren</>}
                    </button>
                    <button onClick={() => sendInvoice(inv)} disabled={sendingId === inv.id || !inv.customer_email || inv.cancelled} data-testid={`send-invoice-${inv.id}`} title={inv.cancelled ? "Stornierte Rechnung kann nicht versendet werden" : (inv.customer_email ? "Rechnung per Mail senden" : "Keine E-Mail hinterlegt")}
                      className="inline-flex items-center gap-2 text-sm rounded-full border border-white/15 text-[#ccc] px-4 py-2 hover:text-white hover:border-white/40 transition-colors disabled:opacity-40">
                      <Send size={15} /> {sendingId === inv.id ? "Sendet…" : "Mail"}
                    </button>
                    <button onClick={() => downloadPdf(inv)} data-testid={`pdf-${inv.id}`} className="inline-flex items-center gap-2 text-sm rounded-full bg-white text-[#050505] px-4 py-2 hover:bg-[#d1d5db] transition-colors">
                      <Download size={15} /> PDF
                    </button>
                    <button onClick={() => remove(inv)} data-testid={`inv-delete-${inv.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
